import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IPagination, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T, pagination?: IPagination) => void;
}

interface Ifilter {
  district_name: string;
  region_name: string;
  is_deleted?: boolean;
}

export const DistrictLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      district_name: "",
      region_name: "",
    });

  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet((p) => ({ ...p, ...data }));
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.district_name.value, form.region_name.value, form.is_deleted]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading} closeOnoutSideClick>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Region Name"
              placeholder="Search by region ..."
              value={form.region_name.value}
              handleChange={updateInputField("region_name")}
            />
            <CustomizedSwitch
              title="deleted"
              onChange={updateInputField("is_deleted", "BOOLEAN")}
              value={form.is_deleted?.value ?? false}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof Ifilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by district name..."
          value={form.district_name.value}
          handleChange={updateInputField("district_name")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};